import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";

const empty = {
  name: "", email: "", phone: "", ghl_sub_account_id: "", ghl_token_ref: "",
  setter_agent_id: "", booking_calendar_id: "", partner_tag: "", home_base: "",
  start_date: "", rep_monthly_cost: "", data_source: "ghl",
  votel_sub_account_id: "", votel_api_key: "", votel_affiliate_code: "",
  votel_tracking_link: "", votel_login_url: "",
};

export default function AddRepDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [model, setModel] = useState("flat_pct");
  const [flatRate, setFlatRate] = useState("");        // % as whole number
  const [flatAmount, setFlatAmount] = useState("");
  const [defaultRate, setDefaultRate] = useState("");
  const [products, setProducts] = useState([{ name: "", rate: "" }]);
  const [recurringMode, setRecurringMode] = useState("one_time");
  const [gated, setGated] = useState(true);
  const [inactivityDays, setInactivityDays] = useState("90");
  const [clawbackDays, setClawbackDays] = useState("30");
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const buildRateConfig = () => {
    if (model === "flat_pct") return { rate: Number(flatRate) / 100 };
    if (model === "flat_amount") return { amount_per_deal: Number(flatAmount) };
    if (model === "per_product") {
      const p = {};
      products.forEach((pr) => { if (pr.name) p[pr.name] = Number(pr.rate) / 100; });
      return { default_rate: Number(defaultRate) / 100, products: p };
    }
    return {};
  };

  const submit = async () => {
    if (!f.name || !f.email) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        rep_monthly_cost: Number(f.rep_monthly_cost) || 0,
        commission_profile: {
          model,
          rate_config: buildRateConfig(),
          recurring_mode: recurringMode,
          residual_gated_on_active_engagement: gated,
          inactivity_freeze_days: Number(inactivityDays) || 90,
          clawback_window_days: Number(clawbackDays) || 30,
          applies_to: ["setup", "mrr", "future_sales"],
        },
      };
      const res = await api.post("/reps", payload);
      toast.success(`${res.data.rep.name} onboarded — commission profile active`);
      setOpen(false);
      setF(empty); setModel("flat_pct"); setFlatRate(""); setFlatAmount("");
      setDefaultRate(""); setProducts([{ name: "", rate: "" }]); setRecurringMode("one_time");
      onCreated && onCreated();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const field = (label, key, opts = {}) => (
    <div className="space-y-1.5">
      <Label className="overline">{label}</Label>
      <Input data-testid={`rep-${key}`} value={f[key]} onChange={set(key)}
        placeholder={opts.ph || ""} type={opts.type || "text"}
        className={opts.mono ? "font-mono" : ""} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-rep-btn" className="gap-2"><Plus size={16} weight="bold" /> Add Rep</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Onboard a Rep</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Every rate is read from this rep's own profile — there is no system default. Fill the terms you agreed with this person.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <section>
            <div className="overline mb-3 text-primary">Identity</div>
            <div className="grid grid-cols-2 gap-4">
              {field("Full Name", "name", { ph: "Jane Doe" })}
              {field("Email", "email", { ph: "jane@example.com", type: "email", mono: true })}
              {field("Phone", "phone", { ph: "555-0100", mono: true })}
              {field("Home Base", "home_base", { ph: "Austin, TX" })}
              {field("Start Date", "start_date", { type: "date" })}
              {field("Monthly Cost ($)", "rep_monthly_cost", { ph: "0", type: "number", mono: true })}
            </div>
          </section>

          <section>
            <div className="overline mb-3 text-primary">Data Source</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="overline">Deal data source</Label>
                <Select value={f.data_source} onValueChange={(v) => setF({ ...f, data_source: v })}>
                  <SelectTrigger data-testid="rep-data-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ghl">Legacy (GHL)</SelectItem>
                    <SelectItem value="votel">LocalPro OS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {f.data_source === "votel" && (
              <div className="mt-4 grid grid-cols-2 gap-4" data-testid="votel-fields">
                {field("LocalPro OS Sub-account ID", "votel_sub_account_id", { mono: true })}
                {field("LocalPro OS API Key", "votel_api_key", { mono: true })}
                {field("LocalPro OS Affiliate Code", "votel_affiliate_code", { mono: true })}
                {field("LocalPro OS Tracking Link", "votel_tracking_link", { mono: true, ph: "https://…" })}
                {field("LocalPro OS Login URL", "votel_login_url", { mono: true, ph: "https://os.localpro.one/…" })}
              </div>
            )}
          </section>

          <section>
            <div className="overline mb-3 text-primary">GHL Provisioning</div>
            <div className="grid grid-cols-2 gap-4">
              {field("GHL Sub-account ID", "ghl_sub_account_id", { mono: true })}
              {field("GHL Token Ref", "ghl_token_ref", { mono: true })}
              {field("Setter Agent ID", "setter_agent_id", { mono: true })}
              {field("Booking Calendar ID", "booking_calendar_id", { mono: true })}
              {field("Partner Tag", "partner_tag", { ph: "partner-jane", mono: true })}
            </div>
          </section>

          <section>
            <div className="overline mb-3 text-primary">Commission Terms</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="overline">Rate Model</Label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid="rep-model"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat_pct">Flat % of income</SelectItem>
                    <SelectItem value="per_product">Per-product rates</SelectItem>
                    <SelectItem value="flat_amount">Flat $ per deal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Recurring Mode</Label>
                <Select value={recurringMode} onValueChange={setRecurringMode}>
                  <SelectTrigger data-testid="rep-recurring"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-time only</SelectItem>
                    <SelectItem value="residual">Residual (monthly)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {model === "flat_pct" && (
              <div className="mt-4 space-y-1.5">
                <Label className="overline">Rate (%)</Label>
                <Input data-testid="rep-flat-rate" type="number" value={flatRate}
                  onChange={(e) => setFlatRate(e.target.value)} placeholder="e.g. 50" className="font-mono" />
              </div>
            )}
            {model === "flat_amount" && (
              <div className="mt-4 space-y-1.5">
                <Label className="overline">Amount per deal ($)</Label>
                <Input data-testid="rep-flat-amount" type="number" value={flatAmount}
                  onChange={(e) => setFlatAmount(e.target.value)} placeholder="e.g. 250" className="font-mono" />
              </div>
            )}
            {model === "per_product" && (
              <div className="mt-4 space-y-3">
                <div className="space-y-1.5">
                  <Label className="overline">Default Rate (%)</Label>
                  <Input data-testid="rep-default-rate" type="number" value={defaultRate}
                    onChange={(e) => setDefaultRate(e.target.value)} placeholder="e.g. 30" className="font-mono" />
                </div>
                {products.map((pr, i) => (
                  <div key={i} className="flex items-end gap-2">
                    <div className="flex-1 space-y-1.5">
                      <Label className="overline">Product</Label>
                      <Input value={pr.name} placeholder="CallBoss"
                        onChange={(e) => { const n = [...products]; n[i].name = e.target.value; setProducts(n); }} />
                    </div>
                    <div className="w-28 space-y-1.5">
                      <Label className="overline">Rate %</Label>
                      <Input type="number" value={pr.rate} className="font-mono"
                        onChange={(e) => { const n = [...products]; n[i].rate = e.target.value; setProducts(n); }} />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setProducts(products.filter((_, x) => x !== i))}>
                      <Trash size={16} />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setProducts([...products, { name: "", rate: "" }])}>
                  <Plus size={14} /> Add product override
                </Button>
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="overline">Inactivity Freeze (days)</Label>
                <Input data-testid="rep-inactivity" type="number" value={inactivityDays}
                  onChange={(e) => setInactivityDays(e.target.value)} className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Clawback Window (days)</Label>
                <Input data-testid="rep-clawback" type="number" value={clawbackDays}
                  onChange={(e) => setClawbackDays(e.target.value)} className="font-mono" />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between rounded-sm border border-border p-3">
              <div>
                <div className="text-sm font-medium">Residuals gated on active engagement</div>
                <div className="text-xs text-muted-foreground">If off, residuals vest after the engagement ends.</div>
              </div>
              <Switch data-testid="rep-gated" checked={gated} onCheckedChange={setGated} />
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="rep-submit" onClick={submit} disabled={saving}>
            {saving ? "Provisioning…" : "Create Rep"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
